import {
  Navigate,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";

import { Billing } from "./billing/Billing";
import { Account } from "./account/Account";
import { Domains } from "./account/Domains";
import { SignedInShell } from "./account/SignedInShell";
import { CheckoutSuccess } from "./marketing/CheckoutSuccess";
import { Landing } from "./marketing/Landing";
import { Legal } from "./marketing/Legal";
import { Login } from "./marketing/Login";
import { McpDocs } from "./marketing/McpDocs";
import { Pricing } from "./marketing/Pricing";
import { Signup } from "./marketing/Signup";
import { SmsConsent } from "./marketing/SmsConsent";
import { UnlockPreview } from "./marketing/UnlockPreview";
import { Workspace } from "./workspace/Workspace";
import { WorkspaceIndex } from "./workspace/WorkspaceIndex";
import type { BillingCycle } from "./marketing/sections/Pricing";

interface SignupSearch {
  business?: string;
  cancelled?: 1;
  credits?: number;
  cycle?: BillingCycle;
  email?: string;
  intent?: string;
  plan?: "pro" | "business";
  slug?: string;
}

interface CheckoutSuccessSearch {
  session_id?: string;
  slug?: string;
}

interface UnlockSearch {
  bad?: 1;
  next?: string;
  site?: string;
}

interface LoginSearch {
  auth?: string;
  slug?: string;
  welcome?: 1;
}

function stringParam(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function flagParam(value: unknown): 1 | undefined {
  return value === "1" || value === 1 ? 1 : undefined;
}

function billingCycleParam(value: unknown): BillingCycle | undefined {
  return value === "monthly" || value === "annual" ? value : undefined;
}

function planParam(value: unknown): "pro" | "business" | undefined {
  return value === "pro" || value === "business" ? value : undefined;
}

function creditsParam(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

function intentParam(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const intent = value.trim().slice(0, 200);
  return intent.length > 0 ? intent : undefined;
}

const rootRoute = createRootRoute();

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Landing,
});

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing",
  component: Pricing,
});
const mcpDocsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/docs/mcp", component: McpDocs });

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  validateSearch: (search: Record<string, unknown>): SignupSearch => ({
    business: stringParam(search.business),
    cancelled: flagParam(search.cancelled),
    credits: creditsParam(search.credits),
    cycle: billingCycleParam(search.cycle),
    email: stringParam(search.email),
    intent: intentParam(search.intent),
    plan: planParam(search.plan),
    slug: stringParam(search.slug),
  }),
  component: Signup,
});

const checkoutSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/checkout-success",
  validateSearch: (
    search: Record<string, unknown>,
  ): CheckoutSuccessSearch => ({
    session_id: stringParam(search.session_id),
    slug: stringParam(search.slug),
  }),
  component: CheckoutSuccess,
});

const unlockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/unlock",
  validateSearch: (search: Record<string, unknown>): UnlockSearch => ({
    bad: flagParam(search.bad),
    next: stringParam(search.next),
    site: stringParam(search.site),
  }),
  component: UnlockPreview,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    auth: stringParam(search.auth),
    slug: stringParam(search.slug),
    welcome: flagParam(search.welcome),
  }),
  component: Login,
});

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  component: () => (
    <SignedInShell active="billing">
      <Billing />
    </SignedInShell>
  ),
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: () => (
    <SignedInShell active="account">
      <Account />
    </SignedInShell>
  ),
});

const domainsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/domains",
  component: () => (
    <SignedInShell active="domains">
      <Domains />
    </SignedInShell>
  ),
});

const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workspaces/$workspaceId",
  component: () => (
    <SignedInShell active="projects" mode="editor">
      <Workspace />
    </SignedInShell>
  ),
});

const workspaceIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workspaces",
  component: () => (
    <SignedInShell active="projects">
      <WorkspaceIndex />
    </SignedInShell>
  ),
});

const legalIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal",
  component: () => (
    <Navigate to="/legal/$doc" params={{ doc: "terms" }} replace />
  ),
});

const smsConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal/sms",
  component: SmsConsent,
});

const legalDocRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal/$doc",
  component: Legal,
});

const routeTree = rootRoute.addChildren([
  landingRoute,
  pricingRoute,
  mcpDocsRoute,
  signupRoute,
  checkoutSuccessRoute,
  unlockRoute,
  loginRoute,
  billingRoute,
  accountRoute,
  domainsRoute,
  workspaceIndexRoute,
  workspaceRoute,
  legalIndexRoute,
  smsConsentRoute,
  legalDocRoute,
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => <Navigate to="/" replace />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
